/**
 * Plain values with no Zod dependency, so the browser bundle can import the
 * enum lists without pulling a validation library along with them.
 * `enums.ts` builds the Zod schemas on top of these.
 */
/**
 * The six kinds of thing a day can be built from. `holiday` is seasonal and
 * date-bound; `stage` means live performance; `wild` means zoos, aquariums,
 * farms and animal encounters as distinct from `trail` hiking.
 */
export const CATEGORIES = ['culture', 'food', 'trail', 'stage', 'wild', 'holiday'];
export const SEASONS = ['spring', 'summer', 'fall', 'winter'];
export const EFFORTS = ['easy', 'moderate', 'strenuous'];
/** Trail shape, from AllTrails. Decides whether a stated mileage is round trip. */
export const ROUTE_TYPES = ['loop', 'out_and_back', 'point_to_point'];
/**
 * What a `costUsd` is made of.
 *
 * Not for arithmetic — knowing a price is per-head does not tell you the head
 * rate, because places charge children differently and the catalog does not
 * record the rate card. It is for knowing whether the number moves at all when
 * the party does:
 *
 *   per_person   a family of three genuinely pays less
 *   per_vehicle  $20 parking is $20 whether three arrive or five
 *   flat         a fixed fee, same reasoning
 *   free         nothing to vary
 *
 * Without it, a household of a different size has to be warned about every
 * priced outing, including the ones whose figure is already exactly right for
 * them — and a caveat that appears everywhere gets read nowhere.
 */
export const COST_BASES = ['per_person', 'per_vehicle', 'flat', 'free'];
/**
 * Days a place is actually open, when that is a restriction worth recording.
 *
 * Monday first, matching how the notes are written rather than how JS numbers
 * them. Null on an outing means no known restriction, not "open nothing" —
 * most of the catalog is a trail or a park with nothing to say here, and
 * requiring a value would be a schema demanding data that does not exist.
 */
export const OPEN_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
/** Never hard-delete an outing — retire it. Plans and visits reference it historically. */
export const LIFE_STATES = ['active', 'verify', 'retired'];
/** Role a stop plays in an assembled day. */
export const SLOTS = ['hero', 'food', 'addon', 'extra'];
/**
 * When in the day a thing happens. This is what decides whether the meal comes
 * before the hero or after it: dinner precedes a symphony, lunch follows a
 * hike. `flexible` means it works whenever, which is most of the catalog.
 */
export const DAY_PARTS = ['morning', 'afternoon', 'evening', 'flexible'];
/** `any` is the pseudo-region the `__picnic` fallback lives in. */
export const ANY_REGION = 'any';
/** The system outing used when a region set contains no real food option. */
export const PICNIC_ID = '__picnic';
/**
 * Derive a slug from a display name. Used when a caller creates an outing
 * without supplying an id.
 */
export function slugify(name) {
    return name
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80);
}
/*
 * Planner defaults live here rather than beside the schema that consumes them,
 * because this module is deliberately Zod-free: the browser imports it to get
 * these numbers without pulling a validation library into the bundle. The
 * schema in `planner.ts` re-exports them so server code has one obvious home.
 */
/** One-way minutes. */
export const DEFAULT_MAX_DRIVE = 90;
/** Whole dollars, total for five. */
export const DEFAULT_BUDGET = 250;
/** Half a day door to door, ground time plus the round trip. */
export const DEFAULT_MAX_HOURS = 6;
/**
 * Where the day starts.
 *
 * Every `driveMinutes` in the catalog is one-way from here, so this is the
 * origin the whole distance model hangs off. It was never written down, and
 * three different approximations ended up in circulation — 39.3115/-77.2605,
 * 39.3376/-77.2611 and 39.3376/-77.2647. On a six-kilometre errand a
 * three-kilometre origin error is most of the journey, and it produced two
 * confident and wrong conclusions about routing accuracy before anyone noticed
 * the numbers were measured from different houses.
 *
 * When households arrive this becomes `households.home_lat` / `home_lon` and
 * this constant seeds the first row. Until then it is the single definition.
 */
export const HOME = Object.freeze({ lat: 39.333515, lon: -77.23139 });
/**
 * How many people every `costUsd` in the catalog was worked out for.
 *
 * Not a guess at an average family: the figures are costed for a specific five,
 * with the ages that decide who pays the adult rate. `poe-house` is $32 because
 * four of them pay $8 and one is under twelve.
 *
 * That is fine while one household uses the catalog and misleading the moment
 * another does, so it is written down rather than implied. Nothing scales it —
 * 158 of the entries record no basis at all, so there is no way to tell an $8
 * head charge from a $20 parking fee, and a formula would be inventing
 * precision. A household of a different size is shown the number with its
 * basis instead.
 */
export const CATALOG_PARTY_SIZE = 5;
/**
 * Should this figure be shown to `partySize` with its basis stated?
 *
 * True when the cost actually varies with the party and this household is not
 * the one it was worked out for — or when nobody has recorded what the cost is
 * made of, which is the honest default for the 158 entries that say nothing.
 */
export function costNeedsPartyCaveat(costUsd, costBasis, partySize) {
    if (costUsd === 0)
        return false;
    if (partySize === CATALOG_PARTY_SIZE)
        return false;
    return costBasis === 'per_person' || costBasis == null;
}
/**
 * Tags that record a decision about the data rather than describe the place.
 *
 * `area-entry`   this pin is deliberately approximate: a corridor or district
 *                with no single address, where a town-centre point is correct.
 * `rough-access` the drive genuinely ends on an unpaved or dead-end way.
 *
 * They share a table with `free`, `kid-favorite` and `teens`, which is fine
 * until something renders the list — and the outing detail does, so the family
 * would read "rough-access" next to "cheap". These are notes to whoever audits
 * the catalog next, not to whoever is choosing a Saturday.
 *
 * Mirrored by `kind = 'audit'` on the tags table, which is what SQL and the
 * audit scripts select on. This list is the one the browser can see without a
 * round trip.
 */
export const AUDIT_TAGS = Object.freeze(['area-entry', 'rough-access']);
export function isAuditTag(tag) {
    return AUDIT_TAGS.includes(tag);
}
/**
 * Strip the query string and fragment from a URL before storing it.
 *
 * AllTrails links arrive from the assistant connector carrying
 * `utm_source=claude&utm_campaign=...&utm_medium=mcp_widget`. Persisting those
 * would serve the family links that attribute their Saturday to a marketing
 * campaign, and the parameters have nothing to do with the trail.
 *
 * Enforced here rather than trusted from the caller, because the caller is
 * whatever pasted the link.
 */
export function canonicalUrl(url) {
    try {
        const parsed = new URL(url);
        parsed.search = '';
        parsed.hash = '';
        return parsed.toString();
    }
    catch {
        return url;
    }
}
/**
 * The season we are actually in, by the meteorological convention.
 *
 * Exists because "any" is the wrong default for the question people ask.
 * Someone opening this on a July afternoon means "what should we do this
 * weekend", and without a season the planner is entitled to answer with the
 * Antietam Illumination — one evening in December. The schema was not wrong;
 * the default was.
 *
 * Deliberately not wired into the constraints schema itself: a time-dependent
 * default would make the planner's own tests give different answers in
 * different months. It belongs at the edges where a person is asking.
 */
export function currentSeason(date = new Date()) {
    const month = date.getMonth(); // 0-11
    if (month <= 1 || month === 11)
        return 'winter';
    if (month <= 4)
        return 'spring';
    if (month <= 7)
        return 'summer';
    return 'fall';
}
/**
 * Parse a bare `YYYY-MM-DD` as a local date.
 *
 * `new Date('2026-09-19')` is UTC midnight, which is the 18th anywhere west of
 * Greenwich — so a planner asked about a Saturday would reason about Friday and
 * silently drop every weekend-only outing. Worth the four lines.
 */
function localDate(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
}
/** The season a date falls in, so a supplied date settles the season. */
export function seasonOf(iso) {
    // Deliberately delegates rather than restating the month boundaries: two
    // definitions of when summer ends would eventually disagree.
    return currentSeason(localDate(iso));
}
/** For prose. "closed on weds" reads like a typo; "Wednesdays" does not. */
export const DAY_NAMES = {
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
    sat: 'Saturday',
    sun: 'Sunday',
};
/** Which day of the week a date falls on, as an OPEN_DAYS value. */
export function weekdayOf(iso) {
    // JS weeks start on Sunday; OPEN_DAYS starts on Monday, matching how opening
    // hours are written down.
    return OPEN_DAYS[(localDate(iso).getDay() + 6) % 7];
}
//# sourceMappingURL=constants.js.map